/**
 * ChatView — ported from `ui/paradex/src/components/terminal/ChatView.tsx`.
 *
 * Host couplings have been lifted out behind the package seams:
 *  - agent state/actions  → `../../providers/contexts`
 *  - host identity/config → `useTerminalConfig()` (`TerminalConfig`)
 *  - wire types           → `@paradigm/terminal-core`
 *
 * Optional host features (generative UI, widgets, positions, subkey card,
 * mcp-app) are gated on `config.features` / `config.capabilities`. When a
 * capability is absent the feature renders a clearly-marked placeholder and an
 * `// INJECTION POINT:` comment marks where the host renderer plugs in.
 */
import {
  ClipboardEvent,
  DragEvent,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';

import type { ChatAttachment } from '@paradigm/terminal-core';
import { countRunningSubtasks } from '@paradigm/terminal-core';

import { CHAT_FONT_SIZE, CHAT_TURN_GAP } from '../../styles/chat-typography';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';

import { useTerminalConfig } from '../../context/config-context';
import { useAgentActions, useAgentState } from '../../providers/contexts';

import ContextMeter from './ContextMeter';
import ModelPicker, { ModelPickerPlaceholder } from './ModelPicker';

import { PairingHelpBanner, TokenMismatchBanner } from './banners';
import {
  describeSyncFailure,
  isPairingRequiredError,
  isTokenMismatchError,
} from './banners/errors';
import {
  buildExamplePrompts,
  deriveGreetingName,
  extractPositionMarkets,
  formatChatDateLabel,
  greetingForHour,
  isTextEntryTarget,
  managesOwnFocus,
} from './chat-utils';
import { Container } from './chat.styles';
import { SendIcon, SlashIcon, StopIcon, PlusIcon } from '../icons';
import {
  buildStatusText,
  findStreamingMessage,
  formatRunningSubtasks,
} from './inFlightStatus';
import {
  attachmentSizeError,
  buildAttachmentAccept,
  classifyAttachmentFile,
  MAX_ATTACHMENTS_PER_MESSAGE,
  processAttachmentFile,
} from './image-attachments';
import { modelSupportsImages } from '../../models/model-capabilities';
import { MessageBubble, UiRenderer } from './messages/MessageBubble';
import { aModalIsOpen } from '../../focus';
import {
  BUILT_IN_SLASH_COMMANDS,
  buildSkillCommandSuggestions,
  buildSlashCommandSuggestions,
  getSlashCommandQuery,
  type SlashCommandSuggestion,
} from './slash-commands';

/**
 * One staged composer attachment. 'processing' = the downscale/thumbnail
 * pipeline is still running (chip shows a placeholder; × cancels by removal —
 * the async result is discarded when its id is gone). 'ready' = `attachment`
 * is set and will ride the next send.
 */
interface PendingAttachment {
  readonly id: string;
  readonly kind: 'image' | 'file';
  readonly status: 'processing' | 'ready';
  readonly fileName?: string;
  readonly attachment?: ChatAttachment;
}

/** Chip badge for non-image files: the uppercased extension, e.g. "PDF". */
function fileBadgeLabel(fileName: string | undefined): string {
  const i = fileName?.lastIndexOf('.') ?? -1;
  const ext = i >= 0 ? fileName!.slice(i + 1).toUpperCase() : '';
  return ext.length > 0 && ext.length <= 4 ? ext : 'FILE';
}

export default function ChatView() {
  const agentState = useAgentState();
  const agentActions = useAgentActions();
  // Host coupling `useWalletView().paradexAddress` → config `accountId`.
  const { accountId } = useTerminalConfig();

  // The auth gate (see Terminal) only mounts this once paired with an account,
  // so accountId is present; guard defensively for isolated test renders.
  if (accountId == null) return null;

  return (
    <ChatInterface
      state={agentState}
      actions={agentActions}
      accountId={accountId}
    />
  );
}

/** Main chat interface */
function ChatInterface({
  state,
  actions,
  accountId,
}: {
  readonly state: ReturnType<typeof useAgentState>;
  readonly actions: ReturnType<typeof useAgentActions>;
  readonly accountId: string;
}) {
  const config = useTerminalConfig();
  const [input, setInput] = useState('');
  // Images staged for the next send. Each appears as a chip the moment it is
  // picked/pasted/dropped ('processing', while the downscale/thumbnail runs)
  // and flips to 'ready' when done — so there is always something visible to
  // cancel. Removing an entry mid-processing discards its result on arrival.
  const [pendingAttachments, setPendingAttachments] = useState<
    readonly PendingAttachment[]
  >([]);
  // Transient per-file rejection notice ("this model can't take images") —
  // paste/drop bypass the picker's accept filter, so rejection must be told,
  // not just enforced.
  const [attachNotice, setAttachNotice] = useState<string | null>(null);
  const attachNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isMultilineInput, setIsMultilineInput] = useState(false);
  const [skillCommands, setSkillCommands] = useState<
    readonly SlashCommandSuggestion[]
  >([]);
  const [slashIndex, setSlashIndex] = useState(0);
  const [manualSlashOpen, setManualSlashOpen] = useState(false);
  // How many of the most-recent messages to actually render. The provider
  // holds the full transcript (up to ~1500); we only mount a window of it so
  // switching into a long chat renders O(window) bubbles instead of O(all),
  // and streaming reconciles the same bounded set. "Load earlier" grows this.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);
  // Sticky once the user has loaded the whole transcript: keeps every message
  // rendered as new ones stream in, so the window can't slip back below the
  // total and make the "Load earlier" button flicker back into view.
  const [showAllMessages, setShowAllMessages] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  /**
   * The scroll container as STATE, not only as a ref.
   *
   * It is not on screen for the whole life of a chat: a chat being read from
   * the agent shows its loading screen first, and the message region — with
   * this container in it — mounts only when the transcript lands. A ref
   * changing does not re-run anything, so the scroll-back listener attached
   * during that window found nothing to attach to, and none of its
   * dependencies change when the transcript arrives: scrolling to the top of
   * a chat opened that way asked for nothing, for the life of the tab.
   */
  const [messageList, setMessageList] = useState<HTMLDivElement | null>(null);
  const attachMessageList = useCallback((el: HTMLDivElement | null) => {
    messageListRef.current = el;
    setMessageList(el);
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const composerResizeObserverRef = useRef<ResizeObserver | null>(null);
  const hasAutoScrolledRef = useRef(false);
  // Suppresses the floating-date pill while the auto-scroll effect is doing
  // programmatic `scrollTop` writes. Without this, every session switch + the
  // mount jump-to-bottom flashes the pill for ~1.2s.
  const suppressDatePillRef = useRef(false);
  // The session id we last force-scrolled to the bottom, and that session's
  // last-seen message count — used to distinguish "switched into a chat"
  // (always jump to bottom) from "new message in the current chat" (only stick
  // when already near the bottom).
  const lastScrolledSessionRef = useRef<string | null>(null);
  const prevMessageCountRef = useRef(0);
  /**
   * Where the viewport was before content was added ABOVE it.
   *
   * Held as a distance from the BOTTOM, because that is the thing a prepend
   * leaves unchanged: rows arriving above the fold grow `scrollHeight` and
   * shove everything the user was reading down by the height of what
   * arrived, so restoring the same distance from the bottom puts them back
   * where they were.
   *
   * Set only where content is genuinely about to shift — asking for earlier
   * messages, by the button or by scrolling to the top — and never
   * speculatively. It used to be written on every scroll near the top,
   * including the ones where the request was refused (a page already in
   * flight, a run streaming, no cursor left), and a stale anchor then sat
   * waiting for any unrelated change to consume it.
   *
   * `awaitingPage` is why one anchor is not enough: an ask that fetches
   * shifts the content twice — once when the rest of the in-memory
   * transcript is revealed, and again when the agent's page arrives — and
   * both shifts have to be pinned by the same gesture.
   */
  const scrollAnchorRef = useRef<{
    readonly distance: number;
    readonly awaitingPage: boolean;
  } | null>(null);

  // Host coupling `useOpenPositionsStore` → `capabilities.positionsFeed`.
  // When absent, positions context is omitted entirely (no markets ⇒ generic
  // example prompts). The feed returns `readonly unknown[]`; we defensively
  // pull a `market` string off each entry.
  const positions = config.capabilities?.positionsFeed?.usePositions();
  const examplePrompts = useMemo(() => {
    const markets = extractPositionMarkets(positions);
    return buildExamplePrompts(markets);
  }, [positions]);

  // NOTE: the Paradex host re-focused the composer when its drawer tab
  // switched to 'chat' (via `useTerminalDrawerView`). That drawer-context is
  // host-specific and has no package seam, so the tab-switch refocus effect is
  // dropped. The connect-time + session-switch focus below covers the common
  // cases.
  // TODO(host-injection): add an optional "active surface" signal to the
  // config if hosts need composer refocus on tab activation.

  // Auto-connect on mount if URL is set but not connected.
  // Intentional mount-only effect: adding state/actions to deps would
  // re-trigger the connection on every status change, causing reconnect loops.
  useEffect(() => {
    if (state.agentUrl != null && state.status === 'disconnected') {
      actions.connect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refocus the composer when the active session changes — unless a modal is
  // up. This runs from an animation frame, so it lands AFTER a dialog that
  // opened in the same commit has focused itself, and would drop the caret
  // behind the scrim. See `aModalIsOpen`.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (aModalIsOpen()) return;
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [state.activeSessionId]);

  // Auto-scroll to bottom on new messages.
  //
  // First fire (mount, with messages already in context from prior session):
  // hide the list, jump to the bottom, then reveal. Without this the user
  // sees the scroll zip through the entire chat history while the drawer
  // slide-in animation is still playing.
  //
  // Subsequent fires (new incoming messages): smooth-scroll via the end anchor.
  useLayoutEffect(() => {
    const list = messageListRef.current;
    if (list == null) return;
    // The full-terminal overlay manages its own scrolling; don't hijack it.
    // Reset the first-fire flag so we re-arm the initial jump-to-bottom when
    // the overlay closes and the message list comes back.
    if (state.fullTerminalUi != null) {
      hasAutoScrolledRef.current = false;
      return;
    }

    // Mute the date-pill scroll listener for the duration of any programmatic
    // scroll this effect does. 300ms covers the rAF chain in the first-fire
    // branch; refs reset on the next effect run, so a leaked timeout is
    // harmless.
    suppressDatePillRef.current = true;
    window.setTimeout(() => {
      suppressDatePillRef.current = false;
    }, 300);

    const switchedSession =
      lastScrolledSessionRef.current !== state.activeSessionId;
    const wasEmpty = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = state.messages.length;

    if (hasAutoScrolledRef.current) {
      // Switched into a different chat — or its transcript just arrived after
      // an empty switch (lazy remote load) — always jump to the bottom so the
      // latest messages are visible, regardless of the prior scroll position.
      if (switchedSession || (wasEmpty && state.messages.length > 0)) {
        lastScrolledSessionRef.current = state.activeSessionId;
        list.scrollTop = list.scrollHeight;
        return;
      }

      // Same chat, new/edited messages: stick to the bottom ONLY when the user
      // is already near it. Otherwise a non-append `messages` change (e.g.
      // approve/deny replacing the array, or a streaming edit) must not yank a
      // scrolled-up reader down. Scroll the inner list — never the page.
      const distanceFromBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distanceFromBottom <= AUTO_SCROLL_NEAR_BOTTOM_PX) {
        list.scrollTop = list.scrollHeight;
      }
      return;
    }

    // First fire: hide → position → reveal after two frames (enough for the
    // drawer to report a stable layout height).
    lastScrolledSessionRef.current = state.activeSessionId;
    list.style.visibility = 'hidden';
    list.scrollTop = list.scrollHeight;

    let frame1 = requestAnimationFrame(() => {
      list.scrollTop = list.scrollHeight;
      frame1 = requestAnimationFrame(() => {
        list.scrollTop = list.scrollHeight;
        list.style.visibility = '';
        hasAutoScrolledRef.current = true;
      });
    });

    return () => cancelAnimationFrame(frame1);
  }, [state.messages, state.fullTerminalUi, state.activeSessionId]);

  // Floating date pill: shows the date of the topmost visible message while
  // the user scrolls, then fades after a short idle. The auto-scroll effect
  // sets `suppressDatePillRef` to filter out its own programmatic scrolls so
  // session switches don't flash the pill.
  const [floatingDateTs, setFloatingDateTs] = useState<number | null>(null);
  const [datePillVisible, setDatePillVisible] = useState(false);
  // Drop any stale date as soon as the session changes — otherwise the prior
  // chat's date would briefly show if its transcript hasn't loaded yet.
  useEffect(() => {
    setFloatingDateTs(null);
    setDatePillVisible(false);
  }, [state.activeSessionId]);
  /**
   * Scrolling to the top asks the agent for the page before the one on
   * screen.
   *
   * `chat.history` answers one bounded page — capped by `limit`, by a 6 MB
   * response budget that discards oldest-first, and by which transcript
   * branch is current — so the first read of a long chat shows its tail and
   * nothing on screen says so. `canLoadOlder` is the gateway's own
   * `hasMore`, and the provider single-flights the request, so firing this
   * on every scroll event near the top is safe.
   *
   * The threshold is generous rather than exact: a page arriving as the user
   * is still scrolling should already be on its way, not start when the top
   * is hit.
   */
  /**
   * The one way earlier messages are asked for. The button and the
   * scroll-to-top gesture both come through here.
   *
   * Two things can happen, in this order, and each is conditional:
   *
   *  - Part of the in-memory transcript is outside the render window, so
   *    reveal all of it. The whole thing rather than a wider slice: the
   *    window is anchored at the TAIL, so widening it by a page still counts
   *    back from the newest message and would leave a prepended head
   *    outside it.
   *  - The agent holds more beyond what is in memory, so fetch a page.
   *
   * Deciding to reveal HERE, at the gesture, is the point. It used to be
   * decided by watching for a page to land, which put the reveal in a second
   * commit after the prepend — so the rows reached the DOM on the commit
   * after the one that had been pinned. Worse, the flag that armed it was
   * set by any fetch and cleared only by a successful reveal, so a page that
   * came back empty left it armed indefinitely and an unrelated reconnect
   * later revealed the whole transcript and jumped to its oldest row,
   * unasked.
   */
  const askForEarlierMessages = useCallback(() => {
    const hiddenInMemory =
      !showAllMessages && visibleCount < state.messages.length;
    const willFetch = state.canLoadOlder && !state.loadingOlder;
    // Nothing to do, and — crucially — no anchor to leave behind. The
    // refusals live here rather than being discovered later, because an
    // anchor written for a request that was declined is a stale one waiting
    // for something unrelated to spend it.
    if (!hiddenInMemory && !willFetch) return;
    const list = messageListRef.current;
    if (list != null) {
      scrollAnchorRef.current = {
        distance: list.scrollHeight - list.scrollTop,
        // Only a gesture that does BOTH shifts the content twice.
        awaitingPage: hiddenInMemory && willFetch,
      };
    }
    if (hiddenInMemory) setShowAllMessages(true);
    if (willFetch) actions.loadOlderMessages();
  }, [
    showAllMessages,
    visibleCount,
    state.messages.length,
    state.canLoadOlder,
    state.loadingOlder,
    actions,
  ]);

  useEffect(() => {
    const el = messageList;
    if (el == null || !state.canLoadOlder) return undefined;
    const onScrollNearTop = () => {
      if (el.scrollTop > 200) return;
      askForEarlierMessages();
    };
    // Only on a real scroll. Firing once on mount would page every chat
    // whose transcript is shorter than the viewport (`scrollTop` is 0 when
    // there is nothing to scroll), fetching a second page nobody asked for.
    // A bounded page is up to 1000 messages or 6 MB, so a chat that actually
    // has more always overflows and can always be scrolled.
    el.addEventListener('scroll', onScrollNearTop, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScrollNearTop);
    };
    // `messageList` rather than the ref, so this re-attaches when the
    // container appears — see its declaration.
  }, [
    messageList,
    state.canLoadOlder,
    state.activeSessionId,
    askForEarlierMessages,
  ]);

  useEffect(() => {
    const el = messageListRef.current;
    if (el == null) return undefined;
    let hideTimer: number | null = null;
    const updateTopDate = () => {
      const rows = el.querySelectorAll<HTMLElement>('[data-msg-ts]');
      if (rows.length === 0) {
        setFloatingDateTs(null);
        return;
      }
      const top = el.getBoundingClientRect().top;
      for (const row of rows) {
        if (row.getBoundingClientRect().bottom > top + 1) {
          const ts = Number(row.dataset.msgTs);
          if (Number.isFinite(ts)) setFloatingDateTs(ts);
          break;
        }
      }
    };
    const onScroll = () => {
      if (suppressDatePillRef.current) return;
      updateTopDate();
      setDatePillVisible(true);
      if (hideTimer != null) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setDatePillVisible(false), 1200);
    };
    updateTopDate();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (hideTimer != null) window.clearTimeout(hideTimer);
    };
  }, [state.messages.length, state.activeSessionId]);

  useEffect(() => {
    if (state.status !== 'authenticated') {
      setSkillCommands([]);
      return;
    }

    let cancelled = false;

    actions
      .request<unknown>('skills.status')
      .then((payload) => {
        if (cancelled) return;
        setSkillCommands(buildSkillCommandSuggestions(payload));
      })
      .catch(() => {
        if (cancelled) return;
        setSkillCommands([]);
      });

    return () => {
      cancelled = true;
    };
  }, [actions, state.status, state.agentUrl]);

  // Only accept input once the gateway has authenticated us. During
  // 'connected'/'authenticating' the handshake is still in flight and any
  // chat.send would be dropped server-side.
  const isConnected = state.status === 'authenticated';
  // Only the explicit in-flight indicator counts as in-flight from the user's
  // POV. `state.processing` is set optimistically in `sendMessage` and now
  // lives for the WHOLE run — it is cleared by the run's own end signals
  // (chat `final`, a terminal state, `lifecycle.end`, an error, a dropped
  // socket), not by the first answer token. So the button reads Stop for as
  // long as the answer is streaming, which is also exactly how long there is
  // something left to stop. Background frames (heartbeat echoes, unprompted
  // system messages) never set processing, so they don't flip it.
  const isProcessing = state.processing != null;

  // Focus the chat input as soon as it becomes enabled, so opening the
  // terminal (e.g. via `~`) lands the cursor in the typing box without an
  // extra click. The textarea has `disabled={!isConnected}`, so a focus
  // call before authentication is a no-op — we wait for isConnected.
  // Guarded like every composer focus-grab (see `aModalIsOpen`): this one
  // re-fires on every reconnect, so a gateway blip while Settings was open
  // yanked focus out of the dialog and behind the scrim.
  useEffect(() => {
    if (isConnected && !aModalIsOpen()) inputRef.current?.focus();
  }, [isConnected]);

  // Sticky composer focus: the chat input is the terminal's default typing
  // target, so when a click moves focus to another *control* (a button, a
  // chip) pull focus straight back. Crucially, we do NOT reclaim focus when
  // the user is selecting text in the transcript — that blur has no
  // `relatedTarget` (focus goes to the document body), and re-focusing the
  // textarea would collapse the selection. Selecting text stays normal; the
  // next keystroke still lands in the composer via the type-ahead listener.
  const handleComposerBlur = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      if (!isConnected) return;
      const next = e.relatedTarget;
      if (next == null) return;
      if (isTextEntryTarget(next)) return;
      if (managesOwnFocus(next)) return;
      const root =
        containerRef.current?.closest('[data-terminal-root]') ?? null;
      if (root != null && !root.contains(next)) return;
      requestAnimationFrame(() => {
        if (document.activeElement !== inputRef.current) {
          inputRef.current?.focus();
        }
      });
    },
    [isConnected],
  );

  // Type-ahead: when the user starts typing while focus is somewhere
  // non-editable (e.g. after selecting transcript text, or clicking empty
  // space), route the keystroke into the composer instead of dropping it.
  useEffect(() => {
    if (!isConnected) return undefined;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      const composer = inputRef.current;
      if (composer == null) return;
      // `managesOwnFocus` covers a focused dialog, but not focus that has
      // fallen to <body> while one is open — type-ahead would then route the
      // keystroke into the composer BEHIND the modal.
      if (aModalIsOpen()) return;
      const active = document.activeElement;
      if (active === composer) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      if (e.key.length !== 1) return;
      if (isTextEntryTarget(active)) return;
      if (managesOwnFocus(active)) return;
      const root =
        containerRef.current?.closest('[data-terminal-root]') ?? null;
      if (
        root != null &&
        active != null &&
        active !== document.body &&
        !root.contains(active)
      ) {
        return;
      }
      e.preventDefault();
      setInput((prev) => prev + e.key);
      composer.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isConnected]);

  // Whether the CURRENT model takes image input — gates every attach
  // affordance. An image that slips through anyway fails in a mode set by
  // OPENCLAW_VISION_PREFIXES (infra/helm/terminal/values.yaml): under the
  // fleet's "*" the provider rejects the turn (visible error); under an
  // id-prefix list openclaw silently offloads the image and the reply
  // ignores it. The gate exists so users hit neither.
  const imagesSupported = modelSupportsImages(
    state.models,
    state.selectedModelId,
  );

  const showAttachNotice = useCallback((msg: string) => {
    setAttachNotice(msg);
    if (attachNoticeTimerRef.current != null) {
      clearTimeout(attachNoticeTimerRef.current);
    }
    attachNoticeTimerRef.current = setTimeout(
      () => setAttachNotice(null),
      4_000,
    );
  }, []);

  useEffect(
    () => () => {
      if (attachNoticeTimerRef.current != null) {
        clearTimeout(attachNoticeTimerRef.current);
      }
    },
    [],
  );

  const addFiles = useCallback(
    (files: readonly File[]) => {
      if (files.length === 0) return;
      // Per-file, per-model gating: text/PDF attach on any model (openclaw
      // extracts their content server-side, best-effort — failures degrade to
      // a path the agent opens with its tools); images need vision.
      const classified = files.map((file) => ({
        file,
        kind: classifyAttachmentFile(file, imagesSupported),
      }));
      const rejected = classified.filter((c) => c.kind == null);
      if (rejected.length > 0) {
        const rejectedImage = rejected.some((c) =>
          c.file.type.startsWith('image/'),
        );
        showAttachNotice(
          rejectedImage
            ? 'The selected model does not support images.'
            : 'Unsupported file type.',
        );
      }
      const typed = classified.filter(
        (c): c is { file: File; kind: 'image' | 'file' } => c.kind != null,
      );
      // Size is known synchronously — reject oversize/empty files with a
      // visible reason instead of a chip that silently vanishes.
      const sizeError = typed
        .map((c) => attachmentSizeError(c.file, c.kind))
        .find((e) => e != null);
      if (sizeError != null) showAttachNotice(sizeError);
      const usable = typed.filter(
        (c) => attachmentSizeError(c.file, c.kind) == null,
      );
      if (usable.length === 0) return;
      // Full and PARTIAL overflow both get a notice — with 2 chips staged and
      // 3 files dropped, one is admitted and the rest must not just vanish.
      // Slicing here also avoids decoding files that can't be staged.
      const available = MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.length;
      if (available <= 0) {
        showAttachNotice(
          `Attachment limit reached (${MAX_ATTACHMENTS_PER_MESSAGE} per message).`,
        );
        return;
      }
      if (usable.length > available) {
        const skipped = usable.length - available;
        showAttachNotice(
          `Attachment limit is ${MAX_ATTACHMENTS_PER_MESSAGE} per message — ${skipped} file${skipped === 1 ? '' : 's'} skipped.`,
        );
      }
      const accepted = usable.slice(0, available).map(({ file, kind }) => ({
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        kind,
      }));
      // Stage a 'processing' chip per file synchronously so each has a
      // visible, cancellable presence before any async work lands. The cap is
      // enforced INSIDE the updater — two same-tick gestures (paste + drop)
      // would both read a stale length otherwise; results for files that lose
      // the race are discarded by the id check below.
      setPendingAttachments((prev) => {
        const slots = MAX_ATTACHMENTS_PER_MESSAGE - prev.length;
        if (slots <= 0) return prev;
        return [
          ...prev,
          ...accepted.slice(0, slots).map(({ id, kind, file }) => ({
            id,
            kind,
            status: 'processing' as const,
            ...(file.name.length > 0 ? { fileName: file.name } : {}),
          })),
        ];
      });
      for (const { id, file, kind } of accepted) {
        void processAttachmentFile(file, kind).then((attachment) => {
          // Unreadable/undecodable file — remove its chip and say why
          // (sizes were pre-checked, so this is a genuine read failure).
          if (attachment == null) {
            showAttachNotice(
              `Could not attach ${file.name.length > 0 ? file.name : 'file'}.`,
            );
          }
          setPendingAttachments((prev) => {
            // Cancelled (× while processing) — drop the finished result.
            if (!prev.some((p) => p.id === id)) return prev;
            if (attachment == null) return prev.filter((p) => p.id !== id);
            return prev.map((p) =>
              p.id === id ? { ...p, status: 'ready' as const, attachment } : p,
            );
          });
        });
      }
    },
    [imagesSupported, pendingAttachments.length, showAttachNotice],
  );

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Staged attachments are validated at pick time, so a model switch could
  // otherwise smuggle an image to a text-only model — where openclaw strips
  // it silently. Drop image-kind chips (including in-flight ones — their
  // async results are discarded by the id check) the moment vision goes away,
  // and say so.
  useEffect(() => {
    if (imagesSupported) return;
    if (!pendingAttachments.some((p) => p.kind === 'image')) return;
    setPendingAttachments((prev) => prev.filter((p) => p.kind !== 'image'));
    showAttachNotice(
      'The selected model does not support images — attached images were removed.',
    );
  }, [imagesSupported, pendingAttachments, showAttachNotice]);

  const handleComposerPaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return;
      e.preventDefault();
      addFiles(files);
    },
    [addFiles],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isConnected) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      setIsDragOver(true);
    },
    [isConnected],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      setIsDragOver(false);
      if (!isConnected) return;
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length === 0) return;
      e.preventDefault();
      addFiles(files);
    },
    [isConnected, addFiles],
  );

  // Send is held while any image is still processing, so a fast Enter after a
  // paste can't race the downscale and silently drop its image.
  const isAttachProcessing = pendingAttachments.some(
    (p) => p.status === 'processing',
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    const attachments = pendingAttachments
      .map((p) => p.attachment)
      .filter((a): a is ChatAttachment => a != null);
    if (trimmed.length === 0 && attachments.length === 0) return;
    if (isAttachProcessing) return;
    actions.sendMessage(
      trimmed,
      attachments.length > 0 ? { attachments } : undefined,
    );
    setInput('');
    setPendingAttachments([]);
    inputRef.current?.focus();
  }, [input, pendingAttachments, isAttachProcessing, actions]);

  const handleExampleClick = useCallback(
    (prompt: string) => {
      if (state.status !== 'authenticated') return;
      actions.sendMessage(prompt);
      setInput('');
      inputRef.current?.focus();
    },
    [actions, state.status],
  );

  const slashCommandQuery =
    getSlashCommandQuery(input) ?? (manualSlashOpen ? '' : null);
  const slashSuggestions = useMemo(
    () =>
      buildSlashCommandSuggestions(
        [...BUILT_IN_SLASH_COMMANDS, ...skillCommands],
        slashCommandQuery,
      ),
    [skillCommands, slashCommandQuery],
  );
  const showSlashCommands =
    isConnected && slashCommandQuery != null && slashSuggestions.length > 0;

  useEffect(() => {
    setSlashIndex(0);
  }, [slashCommandQuery]);

  useEffect(() => {
    if (slashIndex >= slashSuggestions.length) {
      setSlashIndex(Math.max(0, slashSuggestions.length - 1));
    }
  }, [slashIndex, slashSuggestions.length]);

  const selectSlashCommand = useCallback(
    (suggestion: SlashCommandSuggestion) => {
      setInput(suggestion.insertText);
      setManualSlashOpen(false);
      inputRef.current?.focus();
    },
    [],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSlashCommands) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((index) => (index + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(
          (index) =>
            (index - 1 + slashSuggestions.length) % slashSuggestions.length,
        );
        return;
      }
      if (e.key === 'Tab') {
        // Tab still completes-to-input so the user can type arguments
        // after (e.g. `/model <id>`) before sending.
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          selectSlashCommand(suggestion);
        }
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter sends the highlighted suggestion directly — one
        // keystroke from open-menu to executed command for arg-less
        // commands like `/help` or `/model`. We're inside
        // `showSlashCommands`, which requires `isConnected`, so no
        // separate auth gate needed here.
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          actions.sendMessage(suggestion.insertText);
          setInput('');
          setManualSlashOpen(false);
          inputRef.current?.focus();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        setManualSlashOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // The textarea auto-grows via CSS `field-sizing: content` (see ChatInput) —
  // there is no per-keystroke JS height measurement. We still need to know when
  // it has grown past one line so the send/slash buttons pin to the bottom
  // instead of vertically centering; a ResizeObserver reports that in its own
  // batched phase, off the keystroke critical path, and only when the size
  // actually changes. (The previous per-keystroke layout effect read
  // `scrollHeight` on every character, forcing a full-document reflow — that's
  // what caused the input lag this PR fixes.)
  //
  // Attach via a ref callback rather than a mount-only effect: the composer's
  // <textarea> unmounts and remounts when the empty-state layout flips
  // (`CenteredEmpty` <-> `InputArea` are structurally different subtrees), so a
  // one-shot observer would stay bound to the detached node and leave
  // `isMultilineInput` stale after the first send. The callback re-runs on
  // every node-identity change, keeps `inputRef.current` in sync, and survives
  // the reverse flip. The `useCallback([])` identity keeps React from
  // re-invoking it on ordinary re-renders.
  const attachComposer = useCallback((ta: HTMLTextAreaElement | null) => {
    inputRef.current = ta;
    composerResizeObserverRef.current?.disconnect();
    composerResizeObserverRef.current = null;
    if (ta == null || typeof ResizeObserver === 'undefined') return;
    // Pin the initial value so a remount onto pre-filled text (e.g. a restored
    // draft) aligns correctly without waiting for the first resize event.
    setIsMultilineInput(ta.scrollHeight > SINGLE_LINE_TEXTAREA_HEIGHT);
    const ro = new ResizeObserver(() => {
      // scrollHeight is safe to read here: the observer fires after layout is
      // computed, so this returns the cached value without forcing a reflow,
      // and it runs off the typing path (only on an actual size change).
      setIsMultilineInput(ta.scrollHeight > SINGLE_LINE_TEXTAREA_HEIGHT);
    });
    ro.observe(ta);
    composerResizeObserverRef.current = ro;
  }, []);

  // Claude-style centered empty state. When the session has no messages
  // AND no banners to surface, drop the message-list region and float
  // the composer (+ examples + model picker) vertically centered, with a
  // time-of-day greeting above it. The first send flips this off and the
  // composer snaps to its standard bottom position.
  const isEmpty =
    state.messages.length === 0 &&
    state.fullTerminalUi == null &&
    !isTokenMismatchError(state.errorMessage) &&
    !isPairingRequiredError(state.errorMessage);
  // …but an empty transcript is not always an empty conversation. A chat this
  // device has never read paints from an empty cache while the agent's own
  // copy is on its way, and the greeting above is a claim about a chat that
  // may have months of history in it. The wait gets its own screen.
  // The null check is load-bearing: on an account with no chats at all both
  // sides are null, and a bare equality claimed the empty landing was a
  // conversation still loading — for good, since no read was coming.
  const isAwaitingTranscript =
    isEmpty &&
    state.loadingTranscriptFor != null &&
    state.loadingTranscriptFor === state.activeSessionId;
  // The third answer, and the one that was missing. "Loading" and "empty"
  // covered a read in flight and a chat that really has nothing in it — but
  // a read that FAILED on a chat with no local copy fell through to the
  // second, so the app told the user their conversation was empty when what
  // had actually happened is that it could not reach the agent. There is
  // nothing to fall back on in that case, which is what makes it worth its
  // own screen rather than a silent retry on the next open.
  const transcriptReadFailed =
    isEmpty &&
    state.failedTranscriptFor != null &&
    state.failedTranscriptFor === state.activeSessionId;
  // Host profile first (cloud flow: Clerk name → email local part); the
  // gateway-URL derivation only helps on per-user gateway hosts — behind the
  // shared gateway-proxy it would greet everyone as "Gateway".
  const profileName =
    config.user?.displayName?.trim() ||
    config.user?.email?.split('@')[0]?.trim() ||
    '';
  const greetingName =
    profileName.length > 0 ? profileName : deriveGreetingName(state.agentUrl);
  // Snapshot the time-of-day greeting once per session so it can't flip
  // mid-conversation if the user crosses noon / 6pm while the page is open.
  const greetingTime = useMemo(
    () => greetingForHour(new Date().getHours()),
    [],
  );
  const greeting =
    greetingName.length > 0 ? `${greetingTime}, ${greetingName}` : greetingTime;

  // Composer subtree extracted so we can mount it in both layouts without
  // duplicating ~70 lines of JSX. State (input, slash menu, refs) is closed
  // over from the surrounding component scope.
  const composerNode = (
    <ComposerStack
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {attachNotice != null && (
        <AttachmentNotice role="status">{attachNotice}</AttachmentNotice>
      )}
      {pendingAttachments.length > 0 && (
        <AttachmentRow>
          {pendingAttachments.map((p) => (
            <AttachmentChip key={p.id}>
              {p.status !== 'ready' ? (
                <AttachmentProcessing aria-label="Processing attachment" />
              ) : p.attachment?.previewUrl != null ? (
                <AttachmentThumb src={p.attachment.previewUrl} alt="" />
              ) : (
                <AttachmentFileBadge>
                  {fileBadgeLabel(p.fileName)}
                </AttachmentFileBadge>
              )}
              <AttachmentName>{p.fileName ?? 'file'}</AttachmentName>
              <AttachmentRemove
                type="button"
                aria-label={
                  p.status === 'processing'
                    ? 'Cancel attachment'
                    : 'Remove attachment'
                }
                title={
                  p.status === 'processing'
                    ? 'Cancel attachment'
                    : 'Remove attachment'
                }
                onClick={() => removeAttachment(p.id)}
              >
                ×
              </AttachmentRemove>
            </AttachmentChip>
          ))}
        </AttachmentRow>
      )}
      <Composer $dragOver={isDragOver}>
        {showSlashCommands && (
          <SlashCommandMenu role="listbox" aria-label="Slash commands">
            {slashSuggestions.map((suggestion, index) => (
              <SlashCommandOption
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={index === slashIndex}
                $active={index === slashIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectSlashCommand(suggestion);
                }}
                onMouseEnter={() => setSlashIndex(index)}
              >
                <SlashCommandName>{suggestion.label}</SlashCommandName>
                <SlashCommandDescription>
                  {suggestion.description}
                </SlashCommandDescription>
                {suggestion.kind === 'skill' && (
                  <SlashCommandBadge>skill</SlashCommandBadge>
                )}
              </SlashCommandOption>
            ))}
          </SlashCommandMenu>
        )}
        <SlashTriggerButton
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={
            !isConnected ||
            pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
          }
          aria-label="Attach files"
          title={
            imagesSupported
              ? 'Attach images, PDFs, or text files'
              : 'Attach PDFs or text files (this model does not support images)'
          }
          $alignBottom={isMultilineInput}
        >
          <PlusIcon />
        </SlashTriggerButton>
        <SlashTriggerButton
          type="button"
          onClick={() => {
            setManualSlashOpen((prev) => !prev);
            inputRef.current?.focus();
          }}
          disabled={!isConnected}
          aria-label="Show slash commands"
          title="Show slash commands"
          aria-pressed={manualSlashOpen}
          $alignBottom={isMultilineInput}
        >
          <SlashIcon />
        </SlashTriggerButton>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept={buildAttachmentAccept(imagesSupported)}
          multiple
          onChange={(e) => {
            const { files } = e.currentTarget;
            if (files != null && files.length > 0) {
              addFiles(Array.from(files));
            }
            // Reset so re-picking the same file fires change again.
            e.currentTarget.value = '';
          }}
        />
        {/* Fake caret that always blinks, independent of real DOM focus — the
          composer regains keystrokes via the type-ahead listener regardless
          of where focus visually is, so this reflects real behavior rather
          than lying about it. Gated on `isConnected`: while disconnected the
          textarea is `disabled` (can't take real focus, and the type-ahead
          listener bails too), so showing this would advertise a typing
          target that drops every keystroke. Hidden via `Composer:focus-within`
          (CSS only, no JS focus tracking) whenever the real textarea has
          focus, so the two carets never show at once. Absolutely positioned
          inside ChatInputWrap (not a flex sibling) so it never perturbs the
          textarea's own layout/padding. */}
        <ChatInputWrap>
          {isConnected && input.length === 0 && (
            <FakeCaret aria-hidden="true" />
          )}
          <ChatInput
            ref={attachComposer}
            data-chat-input
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (manualSlashOpen && !e.target.value.startsWith('/')) {
                setManualSlashOpen(false);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleComposerBlur}
            onPaste={handleComposerPaste}
            placeholder={
              isConnected ? 'Message your agent...' : 'Connecting...'
            }
            disabled={!isConnected}
            rows={1}
            aria-autocomplete="list"
            aria-expanded={showSlashCommands}
          />
        </ChatInputWrap>
        <SendButton
          type="button"
          onClick={isProcessing ? actions.stopProcessing : handleSend}
          disabled={
            isProcessing
              ? false
              : !isConnected ||
                isAttachProcessing ||
                (input.trim().length === 0 && pendingAttachments.length === 0)
          }
          aria-label={isProcessing ? 'Stop' : 'Send message'}
          $alignBottom={isMultilineInput}
        >
          {isProcessing ? <StopIcon /> : <SendIcon />}
        </SendButton>
      </Composer>
    </ComposerStack>
  );

  const examplesNode =
    state.messages.length === 0 &&
    state.fullTerminalUi == null &&
    isConnected ? (
      <ExamplesRow>
        {examplePrompts.map((ex) => (
          <ExampleChip
            key={ex.prompt}
            type="button"
            onClick={() => handleExampleClick(ex.prompt)}
            title={ex.prompt}
          >
            <ExampleLabel>{ex.label}</ExampleLabel>
            {ex.subtitle.length > 0 && (
              <ExampleSubtitle>{ex.subtitle}</ExampleSubtitle>
            )}
          </ExampleChip>
        ))}
      </ExamplesRow>
    ) : null;

  // The catalog comes only from the gateway, so the picker is genuinely absent
  // until `/api/models` answers. The footer row is rendered UNCONDITIONALLY so
  // its height is reserved from first paint — ChatView mounts as soon as the
  // account is paired, which is before the handshake that gates the fetch, so
  // anything conditional here collapses the row and then pops it back in.
  //
  // `idle` (pre-handshake) is shown as loading rather than as its own state:
  // from the user's side it is the same wait, and distinguishing them would
  // just add a third visual step. ModelPickerPlaceholder keeps its box but
  // stays invisible briefly, so a fast catalog never flashes text at all.
  const modelPickerNode = (
    <ComposerFooter>
      {state.models.length > 0 ? (
        <ModelPicker
          models={state.models}
          selectedId={state.selectedModelId}
          onSelect={actions.setSelectedModel}
        />
      ) : (
        <ModelPickerPlaceholder
          state={
            state.modelsStatus === 'unavailable' ? 'unavailable' : 'loading'
          }
        />
      )}
      {/* Last in the row, to the right of the picker. It holds its own slot
          while the gateway has no reading, so the picker doesn't move when one
          arrives — and it arrives repeatedly, since the provider clears the
          reading on every chat switch and reconnect. */}
      <ContextMeter usage={state.contextUsage} />
    </ComposerFooter>
  );

  // Rendered in BOTH the empty and populated layouts. Living only in the
  // populated branch meant a fresh chat — the state in which the model picker
  // is most used — swallowed every transient error, so a failed model switch
  // reverted the picker with no explanation anywhere.
  const transientErrorNode = state.transientError != null && (
    <TransientErrorToast role="alert">
      {state.transientError}
    </TransientErrorToast>
  );

  // A run failure belongs to a specific chat and does NOT auto-dismiss: the
  // user may have been reading another chat (or nothing) when it happened, so
  // it waits here until they acknowledge it or retry. Rendered in both layouts
  // for the same reason the transient toast is.
  const activeSessionError =
    state.activeSessionId != null
      ? state.sessionErrors[state.activeSessionId]
      : undefined;
  const sessionErrorNode = activeSessionError != null && (
    <SessionErrorBanner role="alert">
      <SessionErrorText>{activeSessionError}</SessionErrorText>
      <SessionErrorDismiss
        type="button"
        aria-label="Dismiss error"
        onClick={() => actions.dismissSessionError()}
      >
        ×
      </SessionErrorDismiss>
    </SessionErrorBanner>
  );

  // Cross-device SETTINGS health. Until this existed every sync failure was
  // silent, and the symptom was another device showing stale state. It now
  // speaks only for the preferences document, because that is the only thing
  // this client still uploads — conversations are read back from the agent.
  // Not dismissable: it clears itself when the condition does, and a banner
  // the user can hide about data not reaching their other devices is a banner
  // they will hide and then report the bug.
  const syncWarning = describeSyncFailure(state.syncStatus);
  const syncWarningNode = syncWarning != null && (
    <SyncWarningBanner role="status" data-sync-warning>
      {syncWarning}
    </SyncWarningBanner>
  );

  // Always render the private-chat notice row at its full height so the
  // centered empty-state composer doesn't jump vertically when switching
  // between persistent and temporary chats. The badge inside flips between
  // visible (temporary) and hidden (otherwise) — the row keeps its slot.
  const isTemporarySession =
    state.sessions.find((s) => s.sessionId === state.activeSessionId)
      ?.isTemporary === true;

  // Reset the render window whenever we switch chats so a freshly-opened
  // session starts by mounting only its most-recent slice. Staged attachments
  // are per-conversation drafts, so drop them too.
  //
  // The scroll anchor goes with them. It belongs to one gesture in one chat,
  // and it used to survive a switch: an anchor left by chat A's scroll to the
  // top was spent by the first thing that moved chat B's head, opening B at
  // an arbitrary offset — and because this restore runs after the
  // jump-to-bottom effect, it won.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_MESSAGES);
    setShowAllMessages(false);
    setPendingAttachments([]);
    scrollAnchorRef.current = null;
  }, [state.activeSessionId]);

  /**
   * A page fetched from the gateway has to enter the render window.
   *
   * There are two windows over one transcript, and they were not connected.
   * The provider holds the whole thing and `chat.history` PREPENDS an older
   * page to it; this component renders only the newest `visibleCount` of it.
   * So scrolling to the top fetched a page of up to 1000 messages, put it in
   * memory and on disk, and rendered none of it — the only visible effect was
   * "Load earlier" appearing, and since `canLoadOlder` stayed true and the
   * scroll never moved, each further gesture pulled another page in behind
   * the fold.
   *
   * Whether to reveal is decided at the gesture, in `askForEarlierMessages`,
   * rather than by watching the transcript for a page to arrive.
   */
  const visibleMessages = useMemo(
    () =>
      showAllMessages || visibleCount >= state.messages.length
        ? state.messages
        : state.messages.slice(-visibleCount),
    [state.messages, visibleCount, showAllMessages],
  );

  /**
   * Put the viewport back after content arrived above the fold.
   *
   * The trigger is the first RENDERED row CHANGING — not the row count
   * changing, which is what this used to watch. Both of the motions worth
   * pinning move that row: revealing more of the in-memory transcript, and
   * prepending a fetched page. The motion that must never spend the anchor
   * does not move it — a streamed reply, or the user's own send, arrives
   * BELOW the fold and leaves the head alone.
   *
   * Two bugs came out of counting rows instead. Any growth spent the anchor,
   * so a reply arriving while a page was still in flight consumed it and
   * yanked the viewport; and the comparison was an inequality rather than a
   * growth test, so switching into a SHORTER chat counted as growth and
   * pinned a list that had shrunk.
   *
   * Nothing here runs on a switch or at mount, because the anchor is null
   * then — so the jump-to-bottom effect owns those, which is what should own
   * them.
   */
  const renderedHeadRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const head = visibleMessages[0]?.id ?? null;
    const headMoved = head !== renderedHeadRef.current;
    renderedHeadRef.current = head;
    if (!headMoved) return;
    const list = messageListRef.current;
    const anchor = scrollAnchorRef.current;
    if (list == null || anchor == null) return;
    list.scrollTop = list.scrollHeight - anchor.distance;
    // A gesture that both revealed and fetched has a second shift coming, so
    // re-measure and hold the anchor for it. Holding is safe on its own
    // terms: only a head move can spend it, so a fetch that never settles
    // costs a retained anchor rather than a viewport that keeps jumping —
    // which is what the previous version's `loadingOlder` hold risked, since
    // the request has no timeout.
    scrollAnchorRef.current = anchor.awaitingPage
      ? { distance: list.scrollHeight - list.scrollTop, awaitingPage: false }
      : null;
  }, [visibleMessages]);
  // Offered while there is anything earlier to see — either in memory and
  // outside the window, or still on the agent. The second half matters once
  // the whole in-memory transcript is revealed: the affordance used to
  // disappear permanently at that point, even with the agent holding more,
  // leaving the scroll gesture as the only way to reach it.
  const hasEarlierMessages =
    state.messages.length > visibleMessages.length ||
    state.canLoadOlder === true;
  // The message region depends only on server-driven state, never on the
  // composer's `input`. Memoizing it means a keystroke (which re-renders
  // ChatInterface via setInput) reuses the exact same element reference, so
  // React bails out of reconciling the entire message list instead of
  // re-running the message `.map` and diffing every bubble on every
  // character. It still recomputes when messages/processing/etc. actually
  // change (e.g. streaming), which is correct.
  const messageRegion = useMemo(
    () =>
      state.fullTerminalUi != null ? (
        <FullTerminalOverlay>
          <FullTerminalBar>
            <FullTerminalTitle>
              {state.fullTerminalUi.ui.mode === 'mcp-app'
                ? (state.fullTerminalUi.ui.title ?? 'Agent view')
                : 'Agent layout'}
            </FullTerminalTitle>
            <SmallButton onClick={actions.dismissFullTerminalUi}>
              Return to chat
            </SmallButton>
          </FullTerminalBar>
          <FullTerminalBody>
            <UiRenderer
              ui={state.fullTerminalUi.ui}
              surfaceId={state.fullTerminalUi.messageId}
              sendUiEvent={actions.sendUiEvent}
            />
          </FullTerminalBody>
        </FullTerminalOverlay>
      ) : (
        <MessageList>
          {isTokenMismatchError(state.errorMessage) &&
            state.agentUrl != null && (
              <TokenMismatchBanner
                agentUrl={state.agentUrl}
                accountId={accountId}
                onReconnect={() => {
                  // The internal agent-url store (built from config) owns
                  // token persistence + session clearing; reconnect routes
                  // through it. See TODO(host-injection) on the setup form
                  // about exposing an explicit token-setter seam.
                  actions.connect();
                }}
              />
            )}
          {isPairingRequiredError(state.errorMessage) && (
            <PairingHelpBanner
              accountId={accountId}
              clientIdPrefix={config.clientIdPrefix}
              onRetry={() => {
                actions.connect();
              }}
            />
          )}
          {/*
            Scroll-back has no button — it fires on scroll — so this row is
            the only thing that says a page is on its way. Without it the
            list simply sat still for the length of the round trip, which
            reads as "there is nothing older" rather than "wait".
          */}
          {state.loadingOlder && (
            <LoadEarlierRow>
              <LoadingOlderNote role="status">
                Loading earlier messages…
              </LoadingOlderNote>
            </LoadEarlierRow>
          )}
          {hasEarlierMessages && (
            <LoadEarlierRow>
              <LoadEarlierButton
                type="button"
                onClick={askForEarlierMessages}
                aria-label="Load earlier messages"
              >
                Load earlier messages
              </LoadEarlierButton>
            </LoadEarlierRow>
          )}
          {visibleMessages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onApprovalRespond={actions.respondToApproval}
              onUiEvent={actions.sendUiEvent}
              sidecarRequest={actions.sidecarRequest}
            />
          ))}
          {/* Rendered whether or not a thinking block is live. `main` hid
              the old row behind one, and rightly: it cycled synonyms and said
              nothing the block did not. This one carries elapsed time and a
              token estimate, which the block does not, so the two are
              complementary rather than redundant. */}
          <InFlightRow />
          <div ref={messagesEndRef} />
        </MessageList>
      ),
    [
      state.fullTerminalUi,
      state.errorMessage,
      state.agentUrl,
      visibleMessages,
      hasEarlierMessages,
      askForEarlierMessages,
      state.loadingOlder,

      accountId,
      config.clientIdPrefix,
      actions,
    ],
  );

  return (
    <Container ref={containerRef}>
      <PrivateChatNoticeRow>
        <TemporaryBadge
          $hidden={!isTemporarySession}
          aria-hidden={!isTemporarySession}
        >
          Private Chat - Not Saved
        </TemporaryBadge>
      </PrivateChatNoticeRow>

      {isEmpty ? (
        <CenteredEmpty>
          {/* The greeting is a claim about the conversation, so while the
              agent's copy of it is still on its way this says that instead.
              The composer stays: a chat that is loading is one you can
              already type into, and hiding it would make a one-second read
              look like the app was unavailable. */}
          {isAwaitingTranscript ? (
            <LoadingTranscript>Loading conversation…</LoadingTranscript>
          ) : transcriptReadFailed ? (
            <TranscriptUnavailable role="status">
              <TranscriptUnavailableText>
                Couldn’t load this conversation.
              </TranscriptUnavailableText>
              <LoadEarlierButton
                type="button"
                onClick={actions.retryTranscriptRead}
              >
                Try again
              </LoadEarlierButton>
            </TranscriptUnavailable>
          ) : (
            <Greeting>{greeting}</Greeting>
          )}
          <InputAreaInner>
            {sessionErrorNode}
            {syncWarningNode}
            {transientErrorNode}
            {composerNode}
            {modelPickerNode}
            {/* Not while the conversation is unaccounted for. The chips are
                suggestions for a chat with nothing in it, and under either
                of the screens above they are a second claim that this is
                such a chat — the exact claim being retracted. */}
            {!isAwaitingTranscript && !transcriptReadFailed && examplesNode}
          </InputAreaInner>
        </CenteredEmpty>
      ) : (
        <>
          <ChatBody ref={attachMessageList}>
            <FloatingDateAnchor>
              <FloatingDatePill
                $visible={datePillVisible && floatingDateTs != null}
              >
                {floatingDateTs != null
                  ? formatChatDateLabel(new Date(floatingDateTs))
                  : ''}
              </FloatingDatePill>
            </FloatingDateAnchor>
            {messageRegion}
          </ChatBody>
          {sessionErrorNode}
          {syncWarningNode}
          {transientErrorNode}
          <InputArea>
            <InputAreaInner>
              {examplesNode}
              {composerNode}
              {modelPickerNode}
            </InputAreaInner>
          </InputArea>
        </>
      )}
    </Container>
  );
}

// --- Styled Components ---

const TransientErrorToast = styled.div`
  margin: 0 16px 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--bg-error, rgba(220, 38, 38, 0.15));
  color: var(--text-error, #ef4444);
  font-size: 0.8rem;
  line-height: 1.4;
  animation: fadeIn 150ms ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * Persistent sibling of TransientErrorToast for run failures. Same visual
 * language, but it carries a dismiss control because nothing retires it on a
 * timer — see `AgentState.sessionErrors`.
 */
const SessionErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 16px 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--bg-error, rgba(220, 38, 38, 0.15));
  color: var(--text-error, #ef4444);
  font-size: 0.8rem;
  line-height: 1.4;
`;

const SessionErrorText = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

/**
 * Same slot and weight as the run-failure banner, in the warning palette: the
 * chat itself is fine, it is the copy on OTHER devices that is behind. No
 * dismiss control — see the sync-health comment where it is rendered.
 */
const SyncWarningBanner = styled.div`
  margin: 0 16px 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: color-mix(
    in srgb,
    var(--warning-color, #ffcb23) 12%,
    transparent
  );
  color: var(--warning-color, #ffcb23);
  font-size: 0.8rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const SessionErrorDismiss = styled.button`
  flex: none;
  padding: 0 2px;
  border: none;
  background: none;
  color: inherit;
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`;

// New-chat empty state: a column that takes the remaining vertical space
// below the PrivateChatNoticeRow and centers its children (greeting +
// composer + examples + model picker). Width-capped to keep the composer
// the same width as in the standard bottom-anchored layout so the
// snap-to-bottom on first send isn't a horizontal jump too.
const CenteredEmpty = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: var(--chat-input-max-width, var(--chat-body-max-width, none));
  margin: 0 auto;
  padding: 24px 0;
  gap: 12px;
`;

const Greeting = styled.h2`
  margin: 0;
  padding: 0 16px;
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  color: var(--text-primary);
  letter-spacing: -0.01em;
`;

// Shown in the greeting's place while the agent's copy of a chat is on its
// way. Deliberately quiet — a spinner would make a one-second read look like
// work, and the composer stays out of the way until there is a conversation
// to reply to.
const transcriptShimmer = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 1; }
  100% { opacity: 0.45; }
`;

const LoadingTranscript = styled.p`
  margin: 0;
  padding: 0 16px;
  font-size: 14px;
  text-align: center;
  color: var(--text-secondary);
  animation: ${transcriptShimmer} 1.6s ease-in-out infinite;
`;

/**
 * Where the loading line goes when the read failed instead.
 *
 * Deliberately not the error styling the banners use: nothing is broken and
 * nothing has been lost — the conversation is on the agent and this device
 * could not reach it this once. The same quiet register as the wait it
 * replaces, plus the one control that resolves it.
 */
const TranscriptUnavailable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
`;

const TranscriptUnavailableText = styled.p`
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: var(--text-secondary);
`;

// Centered "Today" / "Jun 30" pill that floats over the chat scroll area
// while the user is scrolling, then fades after ~1.2s idle. Height is 0 so it
// reserves no layout space; the pill itself overflows downward visually.
const FloatingDateAnchor = styled.div`
  position: sticky;
  top: 8px;
  z-index: 2;
  align-self: center;
  height: 0;
  pointer-events: none;
`;

const FloatingDatePill = styled.div<{ $visible: boolean }>`
  background: var(--bg-color-container);
  border: 1px solid var(--border-color);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  line-height: 1;
  white-space: nowrap;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.18s ease;
`;

const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  /* Scroll the full-width body so the scrollbar sits at the panel's right
     edge; message content stays centered via MessageList's max-width. */
  overflow-y: auto;
  ${THIN_SCROLLBAR}
`;

const TemporaryBadge = styled.span<{ $hidden?: boolean }>`
  padding: 3px 10px;
  border-radius: 999px;
  border: none;
  background: var(--hover-bg);
  font-size: 10px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1;
  white-space: nowrap;
  /* When hidden, keep the badge's exact box so the row's vertical slot is
     reserved — prevents the centered empty-state composer from jumping
     between persistent and temporary chats. */
  visibility: ${(p) => (p.$hidden === true ? 'hidden' : 'visible')};
`;

// Centers the "Private Chat - Not Saved" pill in its own row at the top of
// the chat pane.
const PrivateChatNoticeRow = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 8px 12px 0;
`;

const SmallButton = styled.button`
  padding: 2px 8px;
  border: 1px solid var(--button-color-dark);
  border-radius: 6px;
  background: none;
  color: var(--text-label);
  font-size: 11px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const MessageList = styled.div`
  /* Content column stays centered; the scroll lives on ChatBody so the
     scrollbar hugs the panel's right edge rather than this column. */
  width: 100%;
  max-width: var(--chat-body-max-width, none);
  margin: 0 auto;
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  /* One clear gap between turns does the work bubble borders used to: at this
     size adjacent messages read as separate without extra chrome. */
  gap: ${CHAT_TURN_GAP};
`;

const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  line-height: 1.5;
  max-width: 80%;
`;

const LoadEarlierRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0 12px;
`;

/** The scroll-back affordance: a label, not a control — see its use above. */
const LoadingOlderNote = styled.span`
  padding: 4px 12px;
  font-size: 12px;
  color: var(--text-muted, var(--text-secondary));
`;

const LoadEarlierButton = styled.button`
  padding: 4px 12px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
    color: var(--text-primary);
  }

  &:hover {
    color: var(--text-primary);
    border-color: var(--text-secondary);
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const ProcessingLabel = styled.span`
  background: linear-gradient(
    90deg,
    var(--text-secondary) 0%,
    var(--text-secondary) 35%,
    var(--text-primary) 50%,
    var(--text-secondary) 65%,
    var(--text-secondary) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2.5s linear infinite;
`;

/**
 * The in-flight row: everything shown while a turn is in flight, and nothing
 * when none is.
 *
 * Reads the agent state itself rather than taking it as props, so that a
 * reasoning delta — one every ~90 characters, roughly 350 of them on the 32s
 * run this feature was measured against — spares the memoized transcript,
 * which is the expensive part. As props it invalidated that memo, turning a
 * phase that previously caused ZERO list rebuilds into ~11 a second.
 *
 * Not "this row alone", to be exact: `ChatView` consumes `useAgentState()` and
 * passes `state` down, so the `ChatInterface` body still re-runs on every
 * delta. Making that stop means pushing the state read down past it, which is
 * a bigger change than this row justifies.
 *
 * `role="status"` + `aria-live="polite"` make it an announcement region, so
 * what it exposes to assistive tech is deliberately not what it renders: see
 * `ElapsedThinkingLabel` and `StatusAnnouncement`.
 */
/**
 * How many sub-agent subtasks are running under the chat on screen.
 *
 * Re-read on an interval as well as on state changes, because an entry that
 * ages out (`SUBTASK_STALE_MS`) changes the answer with no push to trigger a
 * render — and a stuck count with nothing to retire it is the failure the
 * ageing exists for. Only while a count is displayed, and a minute is fine
 * for a ten-minute window.
 */
function useRunningSubtaskCount(
  state: ReturnType<typeof useAgentState>,
): number {
  const { runningSubtasks, activeSessionId } = state;
  const [now, setNow] = useState(() => Date.now());
  const displaying = runningSubtasks.size > 0;

  useEffect(() => {
    if (!displaying) return;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [displaying]);

  return countRunningSubtasks(runningSubtasks, activeSessionId, now);
}

function InFlightRow() {
  const state = useAgentState();
  const { processing } = state;
  const runningSubtasks = useRunningSubtaskCount(state);
  // Keyed to the run the indicator belongs to, so a cron turn streaming
  // beside an interactive one cannot be mistaken for this run's answer.
  //
  // Its LENGTH is the answer-character count, read off the message rather
  // than accumulated from frames on purpose: gateways deliver the same text
  // on BOTH `agent` stream=assistant and `chat` state=delta — measured
  // identical at 1,383 characters for one run — so counting frames would
  // double the estimate on any build that sends both. Its EXISTENCE is
  // `isResponding`, which is the fact a flag on `processing` used to shadow
  // and get wrong in both directions.
  const streamingMessage = findStreamingMessage(
    state.messages,
    processing?.runId,
  );
  const subtasksText = formatRunningSubtasks(runningSubtasks);
  // Sub-agents outlive the turn that spawned them — `sessions_yield` ends the
  // parent's run and waits for the children — so the row survives
  // `processing == null` on the subtasks alone. No clock: that run has ended.
  if (processing == null) {
    if (subtasksText == null) return null;
    return (
      <ProcessingRow role="status" aria-live="polite">
        <ProcessingLabel>{subtasksText}</ProcessingLabel>
      </ProcessingRow>
    );
  }
  const isResponding = streamingMessage != null;

  return (
    <ProcessingRow role="status" aria-live="polite">
      <ElapsedThinkingLabel
        label={processing.label}
        startedAt={processing.startedAt}
        thinkingChars={processing.thinkingChars}
        responseChars={streamingMessage?.content.length ?? 0}
        isResponding={isResponding}
        runningSubtasks={runningSubtasks}
      />
      <StatusAnnouncement>
        {isResponding ? 'Responding' : processing.label}
      </StatusAnnouncement>
      {subtasksText != null && (
        // Its own live region, because `role="status"` on the row is atomic:
        // with the count inside the row's announcement, every child start and
        // end re-read the state label too — eight times over on a fan-out of
        // eight. A change here is announced from the nearest region, so
        // subtask churn says only the count.
        <StatusAnnouncement aria-live="polite" aria-atomic="true">
          {subtasksText}
        </StatusAnnouncement>
      )}
      {!isResponding &&
        processing.detail != null &&
        processing.detail.length > 0 && (
          <ProcessingDetail>
            {' — '}
            {processing.detail}
          </ProcessingDetail>
        )}
    </ProcessingRow>
  );
}

/**
 * Live "how long has this been running" indicator for an in-flight run.
 *
 * Replaces a list of synonyms ("Reasoning", "Cogitating", "Ruminating", …)
 * that cycled every 2.5s. That motion signalled liveness but carried no
 * information, and it actively competed with `processing.label`, which
 * already names what the agent is really doing (Thinking / Calling tool /
 * Writing) straight from the gateway's own state. Keeping the real label and
 * adding elapsed time means every pixel here is now load-bearing.
 *
 *     14s · 1.2k tokens · Thinking     working up to an answer
 *     23s · 1.9k tokens                answer streaming
 *
 * The clock runs for the WHOLE run — the wait AND the response. What changes
 * is what sits beside it. Before the answer starts, that is the gateway's own
 * state label plus the token estimate, because nothing else on screen is
 * moving and the user cannot tell work from a hang. Once text is streaming
 * the text is its own progress indicator, so the row drops the label rather
 * than competing with it. The estimate itself keeps running across both
 * phases — see `estimateTokens` for why, and for what it is worth.
 *
 * The rendered string is hidden from assistive tech (`aria-hidden`): it
 * changes once a second, and the row is inside a polite live region, so
 * exposing it would announce the whole status every second for the length of
 * the run. The stable half — the gateway's state label — is announced instead,
 * by the caller.
 *
 * One interval per mounted indicator, restarted only when the run changes.
 */
function ElapsedThinkingLabel({
  label,
  startedAt,
  thinkingChars,
  responseChars,
  isResponding,
  runningSubtasks,
}: {
  label: string;
  startedAt: number;
  thinkingChars: number;
  responseChars: number;
  isResponding: boolean;
  runningSubtasks: number;
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    elapsedSecondsSince(startedAt),
  );

  useEffect(() => {
    // Recompute immediately as well as on the interval: remounts (session
    // switch, StrictMode double-invoke) must not show a stale 0s for a run
    // that has already been going for a while.
    const tick = () => setElapsedSeconds(elapsedSecondsSince(startedAt));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  return (
    <ProcessingLabel aria-hidden="true">
      {buildStatusText({
        elapsedSeconds,
        thinkingChars,
        responseChars,
        label,
        isResponding,
        runningSubtasks,
      })}
    </ProcessingLabel>
  );
}

/**
 * Age of a run in whole seconds, floored rather than rounded so the row reads
 * "0s" for the first full second instead of jumping to "1s" at 500ms. Clamped
 * at zero: `startedAt` is a browser stamp, but a clock adjustment mid-run
 * could still put it in the future, and a negative age is never right.
 */
function elapsedSecondsSince(startedAt: number): number {
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

/**
 * Carries the in-flight row's meaning to assistive tech without the noise.
 *
 * The visible text ticks every second; announcing that through the row's
 * polite live region would talk over everything else for the whole run. This
 * span holds only the part that changes when something actually happens —
 * the gateway's state — so a screen reader hears "Thinking", then "Calling
 * tool", then "Responding", and nothing in between.
 */
const StatusAnnouncement = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const ProcessingDetail = styled.span`
  color: var(--text-secondary);
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 40ch;
`;

const InputArea = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
`;

const ExamplesRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-top: 24px;
  margin-bottom: 12px;
  overflow-x: auto;
  ${THIN_SCROLLBAR}

  /* Drop the suggestions when the viewport is too short — keeps the
     composer pinned above the footer instead of getting clipped. */
  @media (max-height: 600px) {
    display: none;
  }
`;

const ExampleChip = styled.button`
  flex-shrink: 0;
  width: 180px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid var(--button-color-dark);
  border-radius: 8px;
  background: none;
  font-family: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: var(--text-secondary);
  }
`;

const ExampleLabel = styled.span`
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`;

const ExampleSubtitle = styled.span`
  color: var(--text-label);
  font-size: 12px;
  line-height: 1.3;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Fullscreen sets --chat-input-max-width so the composer matches the
// 4-card row width. Drawer doesn't set either var, so this falls back
// to --chat-body-max-width (also unset there), i.e. the drawer input
// fills the full pane width as before.
const InputAreaInner = styled.div`
  width: 100%;
  max-width: var(--chat-input-max-width, var(--chat-body-max-width, none));
  margin: 0 auto;
  /* Side padding matches MessageList's so the composer's edges line up with
     the transcript above it rather than sitting a few px wider. */
  padding: 4px 24px 12px;
`;

// Stacks the staged-attachment chips above the input row and is the
// drag-and-drop target for image files (the whole composer area, not just
// the textarea).
const ComposerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Composer = styled.div<{ $dragOver?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  background: var(--input-bg-color);
  border: 1px solid
    ${({ $dragOver }) =>
      $dragOver === true ? 'var(--text-secondary)' : 'var(--border-color)'};
  border-radius: 12px;

  &:focus-within {
    border-color: var(--text-secondary);
  }
`;

const AttachmentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* Clearance for each chip's × button, which overhangs its top-right. */
  padding-top: 6px;
  padding-right: 6px;
`;

const AttachmentChip = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 220px;
  padding: 3px 6px;
  background: var(--input-bg-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
`;

const AttachmentThumb = styled.img`
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 4px;
`;

const attachmentPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.8; }
`;

/* Placeholder square while the downscale/thumbnail pipeline runs. */
const AttachmentProcessing = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: rgba(var(--color-lightest), 0.15);
  animation: ${attachmentPulse} 1.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Extension badge standing in for a thumbnail on non-image files. */
const AttachmentFileBadge = styled.span`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 4px;
  background: rgba(var(--color-lightest), 0.1);
  color: var(--text-secondary);
  font-family: var(--font-mono, monospace);
  font-size: 0.5rem;
  letter-spacing: 0.02em;
`;

const AttachmentName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

/* Transient rejection notice for per-file gating (auto-dismissed). */
const AttachmentNotice = styled.div`
  color: var(--text-secondary);
  font-size: 0.6875rem;
  font-style: italic;
  padding: 0 2px;
`;

/* Floating unstage/cancel control on the chip's top-right corner. */
const AttachmentRemove = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  background: var(--bg-color-page);
  color: var(--text-secondary);
  font-size: 0.6875rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    border-color: var(--text-secondary);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ComposerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
`;

const SlashCommandMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 2;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-color-page);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  ${THIN_SCROLLBAR}
`;

const SlashCommandOption = styled.button<{ $active: boolean }>`
  display: grid;
  grid-template-columns: minmax(8ch, 18ch) minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active ? 'rgba(var(--color-lightest), 0.1)' : 'transparent'};
  color: var(--text-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(var(--color-lightest), 0.1);
  }
`;

const SlashCommandName = styled.span`
  font-family: var(--font-mono, monospace);
  font-size: 0.75rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SlashCommandDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

const SlashCommandBadge = styled.span`
  padding: 1px 5px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 0.5625rem;
  line-height: 1.4;
`;

// Wraps ChatInput so FakeCaret can overlay it absolutely instead of joining
// the Composer flex row — an extra flex sibling would shift ChatInput's own
// padding/text-start when the caret mounts/unmounts.
const ChatInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const ChatInput = styled.textarea`
  width: 100%;
  min-height: 25px;
  max-height: 200px;
  /* Auto-grow to fit content between min/max-height with no JS measurement.
     Supported in Chromium 123+ / Safari 17.4+ (our PWA + embed targets). On
     older engines it's ignored: the textarea stays at the one-line min-height
     and scrolls internally — degraded but functional, and never laggy. */
  field-sizing: content;
  padding: 5px 0;
  border: none;
  background: transparent;
  color: var(--text-primary);
  /* Same scale as the messages it produces — typing and reading match. */
  font-size: ${CHAT_FONT_SIZE};
  font-family: inherit;
  line-height: 1.55;
  resize: none;
  overflow-y: auto;

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

const caretBlink = keyframes`
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
`;

// Blinks regardless of real DOM focus; hidden via the parent Composer's
// `:focus-within` once the real textarea is actually focused, so the two
// carets never show at once. See usage site for why this exists.
const FakeCaret = styled.span`
  position: absolute;
  /* 1px clear of the placeholder's leading glyph (ChatInput has no left
     padding, so left: 0 would sit the caret directly on top of it). */
  left: 1px;
  top: 50%;
  width: 1px;
  /* Roughly ChatInput's line box (CHAT_FONT_SIZE x line-height 1.55). */
  height: 17px;
  transform: translateY(-50%);
  background: var(--text-primary);
  animation: ${caretBlink} 1s step-end infinite;
  pointer-events: none;

  ${Composer}:focus-within & {
    display: none;
  }
`;

// Threshold separating a one-line composer from a wrapped one, used to switch
// the send/slash buttons from center- to bottom-alignment. Must track
// ChatInput's type scale AND its padding: at CHAT_FONT_SIZE (14px) x
// line-height 1.55 a line box is ~22px, plus 10px vertical padding, so one
// line reports ~32px and two report ~54px. 42 sits mid-gap with room for
// font-metric variation across platforms. Re-derive this whenever either the
// font size or ChatInput's padding changes.
const SINGLE_LINE_TEXTAREA_HEIGHT = 42;

// Render-window sizing. The provider keeps the whole transcript; we only mount
// the most-recent `INITIAL_VISIBLE_MESSAGES` (comfortably more than a
// screenful of scrollback), so chat switches and streaming stay O(window)
// rather than O(full history).
//
// Asking for earlier messages reveals the whole rest of the transcript rather
// than another page of it: the window counts back from the NEWEST message, so
// a wider slice still ends before a prepended head and would leave a fetched
// page invisible however many times the user asked.
const INITIAL_VISIBLE_MESSAGES = 50;

// Distance (px) from the bottom within which the message list is considered
// "pinned" — only then do non-append updates keep auto-scrolling to the end.
const AUTO_SCROLL_NEAR_BOTTOM_PX = 120;

const SendButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 28px;
  height: 28px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 6px;
  background: var(--accent-color);
  color: var(--bg-color-page);
  cursor: pointer;

  &:disabled {
    background: var(--button-color);
    color: var(--text-primary);
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;

const SlashTriggerButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 22px;
  height: 22px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0.7;

  &:hover:not(:disabled) {
    opacity: 1;
    color: var(--text-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const FullTerminalOverlay = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  background: var(--bg-color-container);
`;

const FullTerminalBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--divider-color);
`;

const FullTerminalTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const FullTerminalBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 12px;
  ${THIN_SCROLLBAR}
`;
